/**
 * On-device LLM chassis.
 *
 * v0 stub: declares the interface and a CPU-only fallback that explains
 * what the WebGPU LLM panel WILL do once user opts in. The actual
 * transformers.js import is gated behind explicit user action because:
 *   1. ~1.5GB model download is hostile by default
 *   2. WebGPU has spotty Android coverage in 2026
 *   3. Apple Intelligence iOS 27 MCP path may obviate this entirely
 *
 * v0.2: dynamic-import @huggingface/transformers and wire to vault summary.
 */

import type { Vault } from '../types.ts';

export interface InsightCard {
  title: string;
  body: string;
  severity: 'info' | 'attention' | 'urgent';
}

export function deterministicInsights(v: Vault): InsightCard[] {
  const cards: InsightCard[] = [];

  if (v.nominees.length === 0) {
    cards.push({
      title: 'No nominees recorded',
      body: 'Even one nominee changes everything for your family. Without one, every asset is locked behind succession court for 18 to 36 months. Add your spouse, child, or parent now.',
      severity: 'urgent',
    });
  } else if (v.nominees.length === 1) {
    cards.push({
      title: 'Single nominee is fragile',
      body: 'A backup nominee is recommended. If your primary nominee is unreachable, your assets default to legal heirs, which can split unevenly.',
      severity: 'attention',
    });
  }

  const assetsWithoutNominee = v.assets.filter((a) => a.nominee_ids.length === 0).length;
  if (assetsWithoutNominee > 0) {
    cards.push({
      title: `${assetsWithoutNominee} asset(s) without a nominee`,
      body: 'Every Indian bank, mutual fund, demat account, and insurance policy supports a nominee. Each unset nominee is one extra court visit for your family.',
      severity: 'attention',
    });
  }

  const cryptoCount = v.crypto_seeds.length;
  if (cryptoCount > 0) {
    const incompletelyShamired = v.crypto_seeds.filter((s) => s.shamir_shares_encrypted.length !== 5).length;
    if (incompletelyShamired > 0) {
      cards.push({
        title: `${incompletelyShamired} crypto wallet(s) without Shamir backup`,
        body: 'A single-key crypto wallet is a single-point-of-failure. Split your seed phrase 3-of-5 across nominees; any 3 can recover, no 2 can compromise.',
        severity: 'urgent',
      });
    }
  }

  if (v.last_wishes.length === 0) {
    cards.push({
      title: 'No last wishes recorded',
      body: 'Funeral preferences, organ donation, and digital-asset disposal are the three highest-friction decisions families face. Record yours.',
      severity: 'attention',
    });
  }

  if (!v.dead_man_switch.enabled) {
    cards.push({
      title: 'Dead-man-switch is off',
      body: 'A periodic ping with grace-period release is the cleanest way to ensure your nominees get vault access when needed. Configure 90-day pings.',
      severity: 'info',
    });
  } else {
    const last = new Date(v.dead_man_switch.last_ping_at).getTime();
    const days = Math.round((Date.now() - last) / 86_400_000);
    if (days > v.dead_man_switch.ping_interval_days) {
      cards.push({
        title: 'Dead-man-switch is overdue',
        body: `${days} days since last ping. Grace period is ${v.dead_man_switch.grace_period_days} days. Confirm you are well by pinging now.`,
        severity: 'urgent',
      });
    }
  }

  if (cards.length === 0) {
    cards.push({
      title: 'Vault is in good standing',
      body: 'Re-verify every 90 days. Update on any major life event: marriage, child, property, large investment.',
      severity: 'info',
    });
  }

  return cards;
}

export const llmMeta = {
  v0: 'deterministic rule-based insights',
  v02_planned: '@huggingface/transformers, gemma-2-2b-it-q4, WebGPU',
  apple_intel: 'iOS 27 MCP server in mcp/server.ts',
};
